import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { SlidersHorizontal, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";

const ProductList = () => {
    const [params, setParams] = useSearchParams();
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);

    const categoryParam = params.get("categoria");
    const filterParam = params.get("filter");
    const qParam = (params.get("q") || "").trim().toLowerCase();

    useEffect(() => {
        Promise.all([api.get("/products"), api.get("/categories")])
            .then(([p, c]) => {
                setProducts(p.data);
                setCategories(c.data);
            })
            .finally(() => setLoading(false));
    }, []);

    const filtered = useMemo(() => {
        let list = [...products];
        if (categoryParam) list = list.filter((p) => p.category_slug === categoryParam);
        if (filterParam === "novidades") list = list.filter((p) => p.is_new);
        if (filterParam === "mais-vendidos") list = list.filter((p) => p.is_bestseller);
        if (filterParam === "destaques") list = list.filter((p) => p.is_featured);
        if (qParam) {
            list = list.filter((p) => {
                const hay = `${p.name || ""} ${p.short_description || ""} ${p.description || ""} ${p.category_slug || ""}`.toLowerCase();
                return hay.includes(qParam);
            });
        }
        return list;
    }, [products, categoryParam, filterParam, qParam]);

    const setCategory = (slug) => {
        const np = new URLSearchParams(params);
        if (slug) np.set("categoria", slug);
        else np.delete("categoria");
        setParams(np);
    };

    return (
        <div data-testid="product-list-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
            {/* Breadcrumb */}
            <nav data-testid="breadcrumb" className="flex items-center gap-1.5 text-xs text-brand-inkSoft mb-6">
                <Link to="/" className="hover:text-brand-ink">Início</Link>
                <ChevronRight className="w-3 h-3" />
                <span className="text-brand-ink font-semibold">Produtos</span>
                {categoryParam && (
                    <>
                        <ChevronRight className="w-3 h-3" />
                        <span className="text-brand-ink capitalize">{categoryParam}</span>
                    </>
                )}
            </nav>

            <header className="mb-8 sm:mb-12">
                <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-brand-gold mb-2">Catálogo</div>
                <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-brand-ink tracking-tight">
                    {qParam && <>Resultados para "<span className="text-brand-gold">{qParam}</span>"</>}
                    {!qParam && filterParam === "novidades" && "Novidades"}
                    {!qParam && filterParam === "mais-vendidos" && "Mais Vendidos"}
                    {!qParam && filterParam === "destaques" && "Em Destaque"}
                    {!qParam && !filterParam && (categoryParam ? `Categoria: ${categoryParam}` : "Todos os Produtos")}
                </h1>
                <p className="mt-3 text-brand-inkSoft text-sm sm:text-base max-w-xl">
                    {filtered.length} produto{filtered.length !== 1 ? "s" : ""} encontrado{filtered.length !== 1 ? "s" : ""}. Compra e entrega pelas plataformas oficiais.
                </p>
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-8">
                {/* Sidebar / Filters */}
                <aside data-testid="filters-sidebar" className="lg:sticky lg:top-24 lg:self-start">
                    <div className="flex items-center gap-2 mb-4">
                        <SlidersHorizontal className="w-4 h-4 text-brand-ink" />
                        <h3 className="font-heading font-semibold text-sm uppercase tracking-[0.15em] text-brand-ink">Filtros</h3>
                    </div>
                    {/* Mobile horizontal chips */}
                    <div className="lg:hidden flex gap-2 overflow-x-auto no-scrollbar -mx-4 px-4 pb-2">
                        <button
                            onClick={() => setCategory("")}
                            data-testid="filter-chip-all"
                            className={`shrink-0 px-4 py-2 text-xs font-semibold uppercase tracking-wider border rounded-sm transition-colors ${
                                !categoryParam ? "bg-brand-ink text-white border-brand-ink" : "border-brand-border text-brand-inkSoft hover:border-brand-ink"
                            }`}
                        >
                            Todas
                        </button>
                        {categories.map((c) => (
                            <button
                                key={c.id}
                                onClick={() => setCategory(c.slug)}
                                data-testid={`filter-chip-${c.slug}`}
                                className={`shrink-0 px-4 py-2 text-xs font-semibold uppercase tracking-wider border rounded-sm transition-colors ${
                                    categoryParam === c.slug
                                        ? "bg-brand-ink text-white border-brand-ink"
                                        : "border-brand-border text-brand-inkSoft hover:border-brand-ink"
                                }`}
                            >
                                {c.name}
                            </button>
                        ))}
                    </div>
                    {/* Desktop list */}
                    <ul className="hidden lg:block space-y-1.5">
                        <li>
                            <button
                                onClick={() => setCategory("")}
                                data-testid="filter-list-all"
                                className={`w-full text-left px-3 py-2 text-sm rounded-sm transition-colors ${
                                    !categoryParam ? "bg-brand-ink text-white font-semibold" : "text-brand-inkSoft hover:bg-white"
                                }`}
                            >
                                Todas as categorias
                            </button>
                        </li>
                        {categories.map((c) => (
                            <li key={c.id}>
                                <button
                                    onClick={() => setCategory(c.slug)}
                                    data-testid={`filter-list-${c.slug}`}
                                    className={`w-full text-left px-3 py-2 text-sm rounded-sm transition-colors ${
                                        categoryParam === c.slug ? "bg-brand-ink text-white font-semibold" : "text-brand-inkSoft hover:bg-white"
                                    }`}
                                >
                                    {c.name}
                                </button>
                            </li>
                        ))}
                    </ul>
                </aside>

                {/* Grid */}
                <div>
                    {loading ? (
                        <div className="text-center py-20 text-brand-inkSoft" data-testid="list-loading">Carregando…</div>
                    ) : filtered.length === 0 ? (
                        <div className="text-center py-20 text-brand-inkSoft" data-testid="list-empty">
                            Nenhum produto encontrado nesta categoria.
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-testid="products-grid">
                            {filtered.map((p) => <ProductCard key={p.id} product={p} />)}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProductList;
